/**
 * Baked motion tracks — the per-frame "movement along an edge" fast path.
 *
 * `computeActorPose` (movementPlan.ts) is the source of truth, but it is shaped
 * for clarity, not for being called 60×/sec for every actor: it linearly scans
 * every leg + dwell to find the active one, and `samplePolyline` rebuilds the
 * whole arc-length table from scratch on each call.
 *
 * A `BakedTrack` flattens one MovementPlan into per-actor, time-sorted segments
 * with their cumulative arc-length tables and per-edge bearings precomputed once.
 * `sampleActorInto` then resolves the active segment with a monotonic cursor
 * (amortized O(1) during playback, binary search on a scrub) and interpolates
 * with zero allocation, writing into a caller-owned `MutablePose`.
 *
 * The math mirrors `legPose` / `dwellPose` exactly (same easing, heading blends,
 * flare and arc-length segment selection), so a baked sample is numerically
 * identical to `computeActorPose` — `runMotionSelfChecks` asserts this.
 */
import { EASES, lerpAngle, bearingDeg, type EaseFn } from './easing'
import { compassToSceneRotation } from '../engine/heading'
import type { ActorKind, MotionDwell, MotionLeg, MovementPlan } from './movementPlan'

/** Mutable pose target — reused across frames so sampling never allocates. */
export interface MutablePose {
  /** scene feet */
  x: number
  y: number
  z: number
  /** scene Y-rotation degrees */
  rotationDeg: number
  /** false when the actor is not present at the sampled time (caller hides it). */
  present: boolean
}

export function makePose(): MutablePose {
  return { x: 0, y: 0, z: 0, rotationDeg: 0, present: false }
}

interface BakedSegment {
  startT: number
  endT: number
  leg: MotionLeg | null
  dwell: MotionDwell | null
  /** cumulative arc length, length = path.length (only for legs with ≥2 points). */
  cum: Float64Array | null
  /** per-edge bearing degrees, length = path.length − 1. */
  bearings: Float64Array | null
  /** total arc length (cum[last]). */
  total: number
}

interface BakedActor {
  actorId: string
  kind: ActorKind
  segs: BakedSegment[]
  /** mutable scratch: last resolved segment index (monotonic-cursor fast path). */
  cursor: number
}

export interface BakedTrack {
  eventAllocationId: string
  startT: number
  endT: number
  actors: BakedActor[]
}

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x)

function bakeLegTables(leg: MotionLeg): { cum: Float64Array | null; bearings: Float64Array | null; total: number } {
  const pts = leg.path
  const n = pts.length
  if (n < 2) return { cum: null, bearings: null, total: 0 }
  const cum = new Float64Array(n)
  const bearings = new Float64Array(n - 1)
  let total = 0
  for (let i = 1; i < n; i++) {
    const dx = pts[i][0] - pts[i - 1][0]
    const dz = pts[i][1] - pts[i - 1][1]
    total += Math.hypot(dx, dz)
    cum[i] = total
    bearings[i - 1] = bearingDeg(pts[i - 1], pts[i])
  }
  return { cum, bearings, total }
}

/** Flatten one plan into per-actor, time-sorted baked segments. */
export function bakeTrack(plan: MovementPlan): BakedTrack {
  const byActor = new Map<string, BakedActor>()
  const ensure = (actorId: string): BakedActor => {
    let a = byActor.get(actorId)
    if (!a) {
      const ref = plan.actors.find((x) => x.id === actorId)
      a = { actorId, kind: ref?.kind ?? 'aircraft', segs: [], cursor: 0 }
      byActor.set(actorId, a)
    }
    return a
  }

  for (const leg of plan.legs) {
    const tables = bakeLegTables(leg)
    ensure(leg.actorId).segs.push({
      startT: leg.startT,
      endT: leg.endT,
      leg,
      dwell: null,
      cum: tables.cum,
      bearings: tables.bearings,
      total: tables.total,
    })
  }
  for (const d of plan.dwells) {
    ensure(d.actorId).segs.push({
      startT: d.startT,
      endT: d.endT,
      leg: null,
      dwell: d,
      cum: null,
      bearings: null,
      total: 0,
    })
  }

  const actors: BakedActor[] = []
  for (const a of byActor.values()) {
    a.segs.sort((x, y) => x.startT - y.startT)
    actors.push(a)
  }
  // Keep the canonical actor order from the plan (aircraft, tug, fuel_truck).
  actors.sort(
    (x, y) =>
      plan.actors.findIndex((a) => a.id === x.actorId) - plan.actors.findIndex((a) => a.id === y.actorId)
  )

  return { eventAllocationId: plan.eventAllocationId, startT: plan.startT, endT: plan.endT, actors }
}

/** Bake every plan, keyed list for the per-frame loop. */
export function bakeAllTracks(plans: Record<string, MovementPlan>): BakedTrack[] {
  const out: BakedTrack[] = []
  for (const id in plans) out.push(bakeTrack(plans[id]))
  return out
}

const inSeg = (s: BakedSegment, t: number): boolean => t >= s.startT && t < s.endT

/** Resolve the active segment index for `t`, advancing the actor's cursor. -1 if absent. */
function findSegment(actor: BakedActor, t: number): number {
  const segs = actor.segs
  const n = segs.length
  if (n === 0) return -1
  let i = actor.cursor
  if (i < 0 || i >= n) i = 0
  // Monotonic fast paths: same segment, the next one (forward play), the prior one (rewind).
  if (inSeg(segs[i], t)) return i
  if (i + 1 < n && inSeg(segs[i + 1], t)) return (actor.cursor = i + 1)
  if (i - 1 >= 0 && inSeg(segs[i - 1], t)) return (actor.cursor = i - 1)
  // Binary search: largest segment with startT ≤ t, then bounds-check endT.
  let lo = 0
  let hi = n - 1
  let found = -1
  while (lo <= hi) {
    const m = (lo + hi) >> 1
    if (segs[m].startT <= t) {
      found = m
      lo = m + 1
    } else {
      hi = m - 1
    }
  }
  if (found >= 0 && t < segs[found].endT) return (actor.cursor = found)
  return -1
}

function sampleLegInto(seg: BakedSegment, t: number, out: MutablePose): void {
  const leg = seg.leg as MotionLeg
  const span = leg.endT - leg.startT
  const u = span <= 0 ? 1 : clamp01((t - leg.startT) / span)
  const easeFn: EaseFn = EASES[leg.ease] ?? EASES.inOut
  const e = easeFn(u)

  const pts = leg.path
  const n = pts.length
  let px: number
  let pz: number
  let tangentDeg: number
  if (n === 0) {
    px = 0
    pz = 0
    tangentDeg = 0
  } else if (n === 1) {
    px = pts[0][0]
    pz = pts[0][1]
    tangentDeg = 0
  } else if (seg.total === 0) {
    px = pts[0][0]
    pz = pts[0][1]
    tangentDeg = bearingDeg(pts[0], pts[n - 1])
  } else {
    const cum = seg.cum as Float64Array
    const bearings = seg.bearings as Float64Array
    const target = clamp01(e) * seg.total
    // Smallest edge i with cum[i+1] ≥ target (matches samplePolyline's forward scan), else last edge.
    let lo = 0
    let hi = n - 2
    let idx = n - 2
    while (lo <= hi) {
      const m = (lo + hi) >> 1
      if (cum[m + 1] >= target) {
        idx = m
        hi = m - 1
      } else {
        lo = m + 1
      }
    }
    const segLen = cum[idx + 1] - cum[idx]
    const local = segLen === 0 ? 0 : (target - cum[idx]) / segLen
    const a = pts[idx]
    const b = pts[idx + 1]
    px = a[0] + (b[0] - a[0]) * local
    pz = a[1] + (b[1] - a[1]) * local
    tangentDeg = bearings[idx]
  }

  let heading = leg.headingMode === 'fixed' ? leg.fixedHeadingDeg ?? tangentDeg : tangentDeg
  if (leg.launchHeadingDeg != null) {
    const k = Math.min(1, e / 0.25)
    heading = lerpAngle(leg.launchHeadingDeg, heading, k)
  }
  if (leg.settleHeadingDeg != null) {
    const k = Math.max(0, (e - 0.75) / 0.25)
    heading = lerpAngle(heading, leg.settleHeadingDeg, k)
  }

  const altE = leg.kind === 'final' ? 1 - Math.pow(1 - e, 1.6) : e
  const alt = (leg.altStartFt ?? 0) + ((leg.altEndFt ?? 0) - (leg.altStartFt ?? 0)) * altE

  out.x = px
  out.y = alt
  out.z = pz
  out.rotationDeg = compassToSceneRotation(heading)
  out.present = true
}

/**
 * Sample one actor's pose at sim time `t` into `out` (no allocation). Sets
 * `out.present = false` when the actor is absent at `t` (mirrors `computeActorPose`
 * returning null). Advances the actor's monotonic cursor as a side effect.
 */
export function sampleActorInto(actor: BakedActor, t: number, out: MutablePose): void {
  const idx = findSegment(actor, t)
  if (idx < 0) {
    out.present = false
    return
  }
  const seg = actor.segs[idx]
  if (seg.leg) {
    sampleLegInto(seg, t, out)
  } else {
    const d = seg.dwell as MotionDwell
    out.x = d.position[0]
    out.y = d.altFt ?? 0
    out.z = d.position[1]
    out.rotationDeg = compassToSceneRotation(d.headingDeg)
    out.present = true
  }
}
